import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import MultipleFileUpload from 'app/shared/file-upload/multiple-file-upload';
import { useFormik } from 'formik';
import { Button } from 'edozvola-frontend-lib/dist/ui/components';
import { uploadFilesDefaultValidationSchema } from 'app/shared/file-upload/model/file-upload.schema';
import { uploadProjectDocumentAttachmentQuery } from 'app/edozvola/cases/project-documents/service/project-document.queries';
import { Loader } from 'app/shared/loader/loader';
import { FileUploadModel } from 'app/shared/file-upload/model/file-upload.model';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import error = toast.error;
import success = toast.success;
import { queryClient } from 'app/app';
import ProjectDocumentAttachmentsTable from 'app/edozvola/cases/project-documents/attachment-modal/project-document-attachments-table';
import { TABLE_QUERY_KEYS } from 'app/config/constants';

export type AttachmentModalVariant = 'ADD' | 'VIEW';

export interface IAttachmentModalProps {
  idProjectDocument: string | null;
  setIdProjectDocument: any;
  type: AttachmentModalVariant;
}

const AttachmentModal = (props: IAttachmentModalProps) => {
  const [numbOfFilesLeftToUpload, setNumOfFilesLeftToUpload] = useState(-1);
  const [failedFiles, setFailedFiles] = useState<string[]>([]);

  useEffect(() => {
    if (numbOfFilesLeftToUpload === 0) {
      closeModal();
      showToasts();
    }
  }, [numbOfFilesLeftToUpload]);

  const fileUploadedSuccessfully = (file: FileUploadModel) => {
    setNumOfFilesLeftToUpload(prevState => prevState - 1);
  };

  const fileFailedToUpload = (data: string, file: FileUploadModel) => {
    setFailedFiles(prevState => {
      const newState = prevState;
      newState.push(file.fileName);
      return newState;
    });
    setNumOfFilesLeftToUpload(prevState => prevState - 1);
    if (data.startsWith('Datoteka ')) {
      showToast(toast.error, data);
    }
  };

  const { mutate: uploadAttachment, isPending: isAttachmentUploading } = uploadProjectDocumentAttachmentQuery(
    props.idProjectDocument,
    fileUploadedSuccessfully,
    fileFailedToUpload,
  );

  const filesFormik = useFormik({
    initialValues: { files: [] as FileUploadModel[] },
    validationSchema: uploadFilesDefaultValidationSchema,
    onSubmit: values => uploadFiles(values.files),
  });

  const showToasts = () => {
    if (failedFiles.length > 0) {
      showToast(error, `Dio priloga nije uspješno učitan: ( ${failedFiles.join(', ')} ).`);
    } else {
      queryClient.invalidateQueries({ queryKey: ['project-document-attachments-table'] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.PROJECT_DOCUMENTS_TABLE] });
      showToast(success, 'Prilozi su uspješno učitani.');
    }
  };

  const closeModal = () => {
    filesFormik.resetForm();
    setFailedFiles([]);
    setNumOfFilesLeftToUpload(-1);
    props.setIdProjectDocument(null);
  };

  const uploadFiles = (files: FileUploadModel[]) => {
    setNumOfFilesLeftToUpload(files.length);
    files.forEach(file => uploadAttachment(file));
  };

  return (
    <Modal scrollable isOpen={props.idProjectDocument !== null} centered size={'xl'}>
      <ModalHeader toggle={props.type === 'VIEW' ? closeModal : null}>
        {props.type === 'ADD' ? 'Unos priloga na projektu' : 'Pregled dodanih priloga na projektu'}
      </ModalHeader>
      <ModalBody>
        {props.idProjectDocument && props.type === 'ADD' && (
          <>
            {numbOfFilesLeftToUpload > 0 && <Loader />}
            <MultipleFileUpload formik={filesFormik} />
          </>
        )}
        {props.idProjectDocument && props.type === 'VIEW' && (
          <>
            <ProjectDocumentAttachmentsTable idProjectDocument={props.idProjectDocument} />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {props.type === 'ADD' && (
          <>
            <Button size={'lg'} label={'Odustani'} outline color={'secondary'} onClick={closeModal} />
            <Button
              size={'lg'}
              label={'Spremi'}
              color={'info'}
              isLoading={isAttachmentUploading}
              onClick={() => filesFormik.handleSubmit()}
              disabled={!filesFormik.dirty || !filesFormik.isValid}
            />
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AttachmentModal;
